import { useEffect } from 'react'
import { AlmaAccountCore } from '@almamedia/alma-account-core'
import { AlmaAccountAnalytics } from '@almamedia/alma-account-analytics'
import PropTypes from 'prop-types'
import exact from 'prop-types-exact'
import { almaTunnusConfig } from '../../../config/config'
import { browserHistory } from 'react-router'
import { setAlmaTunnusUser } from './almaUserActions'
import { canUseDOM } from '../../lib/utils'
import { logException, logWarning } from '../../sentry'


const core = canUseDOM ? AlmaAccountCore.getInstance() : null

const analytics = canUseDOM ?  AlmaAccountAnalytics.getInstance({
  clientId: almaTunnusConfig.clientID,
}) : null

let loginInProgress = false
let logoutInProgress = false


const saveOriginPath = () => {
  try {
    window.sessionStorage.setItem('originPath', window.location.pathname + window.location.search)
  } catch (error) {
    logException(error)
  }
}

const restoreOriginPath = () => {
  try {
    const redirectPath = window.sessionStorage.getItem('originPath')
    window.sessionStorage.removeItem('originPath')
    if (redirectPath) {
      browserHistory.replace(redirectPath, browserHistory.getCurrentLocation().pathname)
    }
  } catch (error) {
    logException(error)
  }
}

const authCallback = async (dispatch, isAutoLogin = false) => {
  if (await core.isAuthenticated()) {
    const user = await core.getUserInfo()

    // Send analytics event with autologin source if applicable
    if (isAutoLogin) {
      analytics.sendUserEvent({ user, source: 'auto-login' })
    } else {
      analytics.sendUserEvent({ user })
    }

    // There is an issue in AlmaTunnus where access token that is issued
    // immediately after OTP verification says "email_verified=false".
    // We can check for this and get a valid token by requesting a new
    // token with cacheMode=off. But to avoid consuming call-quotas,
    // we use cacheMode *only* if email_verified is false.
    let accessToken = await core.silentAuthentication()
    const decodedToken = parseJwt(accessToken)
    if (decodedToken['https://almamedia.net/email_verified'] === false) {
      accessToken = await core.silentAuthentication({cacheMode: 'off'})
    }

    // Logins to Ampparit profile with the access token
    await dispatch(setAlmaTunnusUser(accessToken, user.email))

    // Only restore origin path for manual logins, not autologin
    if (!isAutoLogin) {
      restoreOriginPath()
    }
  }
}

/**
 * Parses JTW to object. THIS DOES NOT CHECK TOKEN SIGNATURE OR EXPIRATION!
 * @param {string} token
 * @returns {Object} The parsed JWT payload
 */
function parseJwt (token) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))
    return JSON.parse(jsonPayload)
  } catch (error) {
    logException(error)
    return {}
  }
}


const AlmaLogin = ({ dispatch, loginRequested, logoutRequested, registerRequested, loggedIn }) => {

  useEffect(() => {

    const almaTunnusAction = async () => {
      if (core == null || loginInProgress || logoutInProgress) {
        return
      }

      await core.init({ ...almaTunnusConfig })

      // Handle authentication callback from login/register redirects
      if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
        const authenticatedUser = await core.handleAuthentication()
        if (authenticatedUser) {
          await authCallback(dispatch, false)
        } else {
          logWarning('Authentication failed, no user returned from AlmaTunnus')
        }
        return
      }

      // Login user and redirect to path where login was requested
      if (loginRequested && !loggedIn) {
        loginInProgress = true
        saveOriginPath()
        await core.login({
          authorizationParams: {
            theme: 'light',
            lang: 'fi',
            source: 'ampparit-login',
          },
        })
        return
      }

      // Register new user
      if (registerRequested && !loggedIn) {
        loginInProgress = true
        await core.login({
          authorizationParams: {
            theme: 'light',
            lang: 'fi',
            screen_hint: 'signup',
            source: 'ampparit-register',
          },
        })
        return
      }

      // Logout alma-user after Ampparit logout has completed
      if (logoutRequested && !loggedIn) {
        logoutInProgress = true
        // Send logout event to analytics
        const user = await core.getUserInfo()
        if (user) {
          analytics.sendLogout({
            almaIDHash: user['https://almamedia.net/almaIDHash'],
            connection: user['https://almamedia.net/connection'],
            source: 'ampparit-logout',
          })
        }
        await core.logout(almaTunnusConfig.redirectUri)
        return
      }

      // Daily session renewal for analytics
      const refreshedUser = await core.dailySessionRenewal()
      if (refreshedUser) {
        analytics.sendUserEvent({ user: refreshedUser })
      }

      // Attempt auto-login if not already authenticated
      const isAuthenticated = await core.isAuthenticated()
      if (!isAuthenticated) {
        const autoLoginUser = await core.autologin()
        if (autoLoginUser) {
          await authCallback(dispatch, true)
        }
      }
    }

    almaTunnusAction().catch((error) => logException(error))
  }, [dispatch, loginRequested, logoutRequested, registerRequested, loggedIn])

  return null
}

AlmaLogin.propTypes = exact({
  dispatch: PropTypes.func.isRequired,
  loggedIn: PropTypes.bool.isRequired,
  loginRequested: PropTypes.bool,
  logoutRequested: PropTypes.bool,
  registerRequested: PropTypes.bool,
})

export default AlmaLogin
